import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {t} from 'i18next';
import update from 'immutability-helper';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import {DashboardPanelData} from '../../../interfaces/Dashboard';
import {
  MachineStatusReportItem,
  MachineStatusReportQuery,
  MachineStatusReportResponse,
} from '../../../interfaces/MachineStatusReport';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {
  DataGridColumn,
  DataGridRef,
  defaultValueFormatter,
  RenderCellParams,
} from '../../common/DataGrid';
import TruncatedText from '../../common/TruncantedText';
import {
  usePanel,
  usePanelFilter,
} from '../../dashboards/entities/DashboardEntityContext';
import {MachineTypeSelect} from '../../selectors/MachineTypeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: DashboardPanelData;
  onUpdate?: (value: DashboardPanelData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'name',
  'zone_name',
  'type_name',
  'external_id',
  'status',
  'last_update',
  'date',
  'gen1_comm',
  'gen2_comm',
  'gen3_comm',
  'gen4_comm',
  'gen1_lowvoltage',
  'gen2_lowvoltage',
  'gen3_lowvoltage',
  'gen4_lowvoltage',
  'version',
  // 'version_master',
  // 'version_data',
  // 'version_pingecho',
  // 'version_gen1',
  // 'version_gen2',
  // 'version_gen3',
  // 'version_gen4',
];

export interface MachineStatusReportData {
  refreshInterval?: number | null;
  params?: {
    page?: number;
    limit?: number;
    order?: string | null;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export const getMachineStatusReportData = (): MachineStatusReportData => ({
  params: {},
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

const getGenValue = (v?: string | null) => {
  if (v === 'yes') {
    return 'low';
  } else if (v === 'no') {
    return 'ok';
  }
  return v;
};

const renderGenCell = (
  params: RenderCellParams<MachineStatusReportItem>,
  title: string
) => {
  const formattedValue = defaultValueFormatter(params);
  return (
    <Tooltip
      title={
        <Box fontSize={14}>
          {title}:{' '}
          <Box component="span" fontWeight={600}>
            {formattedValue}
          </Box>
        </Box>
      }
    >
      <Box>
        <TruncatedText
          title={formattedValue?.toString()}
          sx={{
            '@media print': {
              whiteSpace: 'normal',
            },
          }}
        >
          {formattedValue}
        </TruncatedText>
      </Box>
    </Tooltip>
  );
};

export const MachineStatusReport = ({value, onUpdate}: Props) => {
  const [panel] = usePanel();
  const {filter, setFilter} = usePanelFilter();
  const config = useMemo(() => {
    return value?.params ? value : getMachineStatusReportData();
  }, [value]);

  /*********/
  /* fetch */
  /*********/

  const [fetchedData, setFetchedData] = useState<MachineStatusReportResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const params = useMemo<MachineStatusReportQuery>(
    () => ({
      page: config.params?.page ?? 0,
      limit: config.params?.limit ?? 25,
      order: config.params?.order ?? null,
      dir: config.params?.dir ?? null,
      type_id: config.params?.type_id ?? null,
    }),
    [config]
  );

  const fetchData = useCallback(
    async (params: MachineStatusReportQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/report/machine-status`;
        const resp = await API.get<MachineStatusReportResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
        setHasPendingChanges(false);
        setIsFirstLoad(false);
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_2680c4e2');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  const showPendingChangesWarning = useMemo(
    () => hasPendingChanges && filter.autoReload === false,
    [hasPendingChanges, filter.autoReload]
  );

  // const handleRefresh = useCallback(() => {
  //   if (params) {
  //     fetchData(params);
  //     setHasPendingChanges(false);
  //   }
  // }, [params, fetchData]);

  const handleToggleAutoReload = useCallback(() => {
    const isCurrentlyEnabled = filter.autoReload !== false;
    setFilter(
      update(filter, {
        autoReload: {
          $set: !isCurrentlyEnabled,
        },
      })
    );
  }, [filter, setFilter]);

  useEffect(() => {
    if (params) {
      if (filter.autoReload !== false || isFirstLoad) {
        fetchData(params);
      } else if (!isFirstLoad) {
        setHasPendingChanges(true);
      }
    }
  }, [params, filter.autoReload]);

  /****************/
  /* auto refresh */
  /****************/
  useRefreshInterval(() => fetchData(params), config?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const shownFields = useMemo(
    () => config?.shownFields ?? value?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [config?.shownFields, value?.shownFields]
  );

  const columnWidths = useMemo(
    () => config?.columnWidths ?? value?.columnWidths ?? {},
    [config?.columnWidths, value?.columnWidths]
  );

  const columnOrder = useMemo(
    () => config?.columnOrder ?? value?.columnOrder,
    [config?.columnOrder, value?.columnOrder]
  );

  const dataGridRef = useRef<DataGridRef>(null);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<MachineStatusReportItem>[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 60,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'zone_name',
      headerName: 'Section',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'type_name',
      headerName: 'Type',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'external_id',
      headerName: 'Proximity ID',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'status',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'last_update',
      headerName: 'Last Event Time',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'date',
      headerName: 'Last Reported',
      width: 160,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'gen1_comm',
      headerName: 'G1',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen1_comm),
      renderCell: (params) => renderGenCell(params, 'Generator #1 Comm Status'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'fail' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen2_comm',
      headerName: 'G2',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen2_comm),
      renderCell: (params) => renderGenCell(params, 'Generator #2 Comm Status'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'fail' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen3_comm',
      headerName: 'G3',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen3_comm),
      renderCell: (params) => renderGenCell(params, 'Generator #3 Comm Status'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'fail' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen4_comm',
      headerName: 'G4',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen4_comm),
      renderCell: (params) => renderGenCell(params, 'Generator #4 Comm Status'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'fail' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen1_lowvoltage',
      headerName: 'FV1',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen1_lowvoltage),
      renderCell: (params) =>
        renderGenCell(params, 'Generator #1 Feedback Voltage'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'low' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen2_lowvoltage',
      headerName: 'FV2',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen2_lowvoltage),
      renderCell: (params) =>
        renderGenCell(params, 'Generator #2 Feedback Voltage'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'low' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen3_lowvoltage',
      headerName: 'FV3',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen3_lowvoltage),
      renderCell: (params) =>
        renderGenCell(params, 'Generator #3 Feedback Voltage'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'low' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'gen4_lowvoltage',
      headerName: 'FV4',
      width: 80,
      resizeable: true,
      sortable: true,
      valueGetter: (params) => getGenValue(params.row.gen4_lowvoltage),
      renderCell: (params) =>
        renderGenCell(params, 'Generator #4 Feedback Voltage'),
      getSxCell: ({value}) => ({
        fontWeight: value === 'low' ? 'bold' : 'normal',
      }),
    },
    {
      field: 'version',
      headerName: 'Controller Version',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_master',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_data',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_pingecho',
      width: 140,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_gen1',
      headerName: 'Version Gen 1',
      width: 130,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_gen2',
      headerName: 'Version Gen 2',
      width: 130,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_gen3',
      headerName: 'Version Gen 3',
      width: 130,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'version_gen4',
      headerName: 'Version Gen 4',
      width: 130,
      resizeable: true,
      sortable: true,
    },
  ];

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    onUpdate?.(
      update(config, {
        columnWidths: {$set: widths},
      })
    );
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      onUpdate?.(
        update(config, {
          shownFields: {$set: fields},
        })
      );
    },
    [onUpdate, config]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      onUpdate?.(
        update(config, {
          columnOrder: {$set: order},
          shownFields: {$set: fields},
        })
      );
    },
    [onUpdate, config]
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      gap={1}
      width="100%"
      overflow="hidden"
    >
      <DashboardPanelTitleSlot>
        {t(`panels.${panel?.code}`)}
      </DashboardPanelTitleSlot>

      <Backdrop open={fetchedInProgress} sx={{position: 'absolute'}}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box display="flex" flexDirection="column" pb={2}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Box display="flex" py={1} gap={1}>
            <MachineTypeSelect
              value={params.type_id}
              label="Machine Type"
              size="small"
              fullWidth
              sx={{minWidth: 200}}
              onChange={(v) =>
                onUpdate?.(
                  update(config, {
                    params: {
                      type_id: {
                        $set: v,
                      },
                    },
                  })
                )
              }
            ></MachineTypeSelect>
          </Box>

          <Box display="flex">
            <Box display="flex" overflow="auto" py={1} gap={1} />

            <ButtonGroup>
              <Tooltip title="Reload">
                <Button
                  size="small"
                  onClick={() => params && fetchData(params)}
                >
                  <RefreshIcon />
                </Button>
              </Tooltip>

              <Tooltip
                title={
                  filter.autoReload === false
                    ? 'Auto-reload disabled: Use refresh button to apply filter changes'
                    : 'Auto-reload enabled: Report reloads when filters change'
                }
              >
                <Button
                  size="small"
                  variant={
                    filter.autoReload === false ? 'outlined' : 'contained'
                  }
                  onClick={handleToggleAutoReload}
                  sx={{minWidth: 'auto'}}
                >
                  <SyncIcon />
                </Button>
              </Tooltip>

              <AutoRefreshSelect
                value={config?.refreshInterval ?? null}
                onChange={(v) => {
                  onUpdate?.(
                    update(config, {
                      refreshInterval: {
                        $set: v,
                      },
                    })
                  );
                }}
              />
              <Button
                size="small"
                onClick={() => dataGridRef.current?.printTable()}
              >
                <PrintIcon />
              </Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Box>

      {showPendingChangesWarning && (
        <Alert severity="warning" sx={{mb: 1}}>
          Auto-reload is disabled. Click the refresh button to apply filter
          changes.
        </Alert>
      )}

      {fetchedErrors.map((error, idx) => (
        <Alert
          key={idx}
          severity="error"
          onClose={() => params && fetchData(params)}
        >
          {error}
        </Alert>
      ))}

      <DataGrid
        ref={dataGridRef}
        rows={rows}
        columns={columns}
        loading={fetchedInProgress}
        shownFields={shownFields}
        defaultShownFields={DEFAULT_SHOWN_FIELDS}
        columnWidths={columnWidths}
        columnOrder={columnOrder}
        pagination
        paginationMode="server"
        size="small"
        sortBy={
          params?.order
            ? {
                field: params?.order,
                dir: params?.dir === 'DESC' ? 'desc' : 'asc',
              }
            : null
        }
        sortingMode="server"
        page={params?.page}
        pageSize={params?.limit}
        rowCount={fetchedData?.total}
        sxFooter={{
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
        }}
        getSxTr={(row) => {
          if (
            row.gen1 === 'fail' ||
            row.gen2 === 'fail' ||
            row.gen3 === 'fail' ||
            row.gen4 === 'fail' ||
            row.gen1_lowvoltage === 'low' ||
            row.gen2_lowvoltage === 'low' ||
            row.gen3_lowvoltage === 'low' ||
            row.gen4_lowvoltage === 'low'
          ) {
            return (theme) => ({
              color: theme.palette.error.main,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? undefined
                  : '#febfbe !important',
            });
          }
        }}
        onPageChange={(v) => {
          onUpdate?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: v,
                },
              },
            })
          );
        }}
        onPageSizeChange={(v) => {
          onUpdate?.(
            update(config, {
              params: {
                $set: {
                  ...config.params,
                  page: 0,
                  limit: v,
                },
              },
            })
          );
        }}
        onSort={(v) => {
          if (v) {
            onUpdate?.(
              update(config, {
                params: {
                  $set: {
                    ...config.params,
                    order: v.field,
                    dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                  },
                },
              })
            );
          }
        }}
        onShownFieldsChange={handleShownFieldsChange}
        onColumnWidthsChange={handleColumnWidthChange}
        onColumnOrderAndShownFieldsChange={handleColumnOrderAndShownFieldsChange}
      />
    </Box>
  );
};
